import type { VariantStatsBasis, VariantStatsRow, VariantStatsView } from '../../../api/client.ts';

export const BASIS_LABELS: Record<VariantStatsBasis, string> = { assigned: 'Assigned (intent-to-treat)', exposed: 'Received the message' };
/** Relative minimum detectable effects offered; the API takes 1 %…500 %. */
export const MDE_OPTIONS = [0.02, 0.05, 0.1, 0.2, 0.3, 0.5, 1] as const;

export const formatPercent = (value: number, digits = 1) => `${(value * 100).toFixed(digits)} %`;
const signed = (value: number, text: string) => `${value > 0 ? '+' : value < 0 ? '−' : ''}${text}`;
/** `+15.0 pp` — an absolute difference of rates in percentage points. */
export const formatPoints = (value: number | null) => (value === null ? '—' : signed(value, `${Math.abs(value * 100).toFixed(1)} pp`));
/** `+75.0 %` — a relative change. */
export const formatUplift = (value: number | null) => (value === null ? '—' : signed(value, `${Math.abs(value * 100).toFixed(1)} %`));
/** `0.012`, `< 0.001`. */
export const formatP = (value: number | null) => (value === null ? '—' : value < 0.001 ? '< 0.001' : value.toFixed(3));
export const formatInterval = (ci: [number, number] | null) => (ci === null ? '—' : `${formatPercent(ci[0])} – ${formatPercent(ci[1])}`);

export const groupName = (row: Pick<VariantStatsRow, 'name' | 'variantApiId' | 'isControl'>) => (row.isControl ? 'Control' : (row.name ?? `${row.variantApiId} (not in the current revision)`));

export function conversionLabel(c: VariantStatsView['conversion']): string {
  const what = c.kind === 'session_start' ? 'session start' : (c.event ?? 'custom event');
  const days = c.windowSeconds / 86_400;
  const window = Number.isInteger(days) ? `${days} ${days === 1 ? 'day' : 'days'}` : `${Math.round(c.windowSeconds / 3_600)} h`;
  return `${what} within ${window}${c.primary ? ' (primary)' : ''}`;
}

/** The verdict in one sentence: who won, or why nobody has yet. */
export function verdictText(data: VariantStatsView): string {
  const required = data.requiredPerVariant === null ? null : data.requiredPerVariant.toLocaleString('en-US');
  switch (data.verdict) {
    case 'winner': {
      const row = data.rows.find((r) => r.variantApiId === data.winner);
      if (row?.isControl) return 'Control wins: every variant converts significantly worse than no message, with enough users in each group.';
      return `Winner: ${row ? groupName(row) : data.winner} — significantly better than control after Holm correction, with enough users in both groups.`;
    }
    case 'no_difference':
      return 'No significant difference from control: every group reached the minimum sample.';
    case 'insufficient_sample':
      return required ? `No winner yet: control and every variant need at least ${required} users each for this minimum detectable effect.` : 'No winner yet: the minimum sample cannot be estimated until control converts.';
    case 'no_control':
      return 'No control group: rates and intervals only, without uplift or a winner.';
    case 'no_data':
      return 'No users have been assigned to a variant yet.';
  }
}
